import { ChangeDetectionStrategy, Component, HostListener, ViewChild } from '@angular/core';
import { VALUE_ENTER, VALUE_ESCAPE } from '@wizeoo/akm-core-lib';
import { WizBasePopUp, WizEvent, WizTextInput } from '@wizeoo/akm-component-lib';

import { IWidget } from '../../../manager/widgets/i-widget';
import { WIDGET_GLOBAL_KIND, WIDGET_KIND } from '../../../model/enums';
import { Model } from '../../../model/model';

export interface WidgetDisplayRights {
  widget: IWidget; // le widget source
  editionAllowed: boolean; // édition autorisée
  headerMassEdit: boolean; // massEdit depuis les headers
  settingsAllowed: boolean; // accès à la configuration 
  showFilters: boolean; // les filtres sont visibles par l'utilisateur

  // mode formulaire, fonctions
  showList: boolean; // read
  showAdd: boolean; // create
  showDelete: boolean; // delete
  gatlingMode: boolean; // mode rafale

  useScenarii: boolean; // utiliser les scenarii sur ce widget
  multipleScenarii: boolean; // utiliser les scenarii multiples dans ce widget
  userShowScenarii: boolean; // visible pour les utilisateurs
  showScenLabel: boolean; // afficher le libellé du scenario

  excelUpdate: boolean; // drop excel pour update
  excelCreate: boolean; // drop excel pour create
  excelSheet?: string; // nom de la feuille excel
  excelNonUniqueShortCode: boolean; // pas de recherche d'unicité par shortCode
  massDel: boolean; // autoriser la suppresssion en masse
  noUserFilter: boolean; // pas de filtre user (role / profil)
  expPDF: boolean; // export Excel autorisé (si possible)
  expXLS: boolean; // export PDF autorisé (si possible)
  hidden: boolean; // widget masqué
  targetPlace: string; // injection dans MENU ou RIGHTP
}

/**
 * Pop de sélection du périmètre de données du widget (Vision, vision step, step perimeter)
 */
@Component({
  templateUrl: './widget-display-rights.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetDisplayRightsPop extends WizBasePopUp {
  
  @ViewChild('tiExcelSheet', { read: WizTextInput, static: true }) tiExcelSheet: WizTextInput;
  @ViewChild('tiTargetPlace', { read: WizTextInput, static: true }) tiTargetPlace: WizTextInput;

  // validation sur enter, sortie sur Esc
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    
    if (event.key === VALUE_ENTER) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.onSave();
    }
    if (event.key === VALUE_ESCAPE) {
      event.stopImmediatePropagation();
      event.preventDefault();
      this.hide();
    }
  }  


  public static popHeight: string = '620px';
  public static popWidth: string = '350px';


  public get excelSheetName(): string {
    return this._settings?.excelSheet || null
  }


  public get isVisible(): boolean {
    return !!this._settings && (this._settings.widget.widgetKind === WIDGET_KIND.PIVOT || this._settings.widget.widgetKind === WIDGET_KIND.GRID);
  }

  /** accès à la configuration excel */
  public get isExcelVisible(): boolean {
    
    if(!this._settings.editionAllowed) {
      return false;
    }
    
    return !!this._settings && (this._settings.widget.widgetKind === WIDGET_KIND.PIVOT || 
      this._settings.widget.widgetKind === WIDGET_KIND.FD_GRID);
  }

  public get isExcelFreedata(): boolean {
    return this.isExcelVisible && this._settings.widget.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA;
  }

  public get model(): Model {
    return this._settings.widget.model;
  }

  private get _settings(): WidgetDisplayRights {
    return this.datas as WidgetDisplayRights;
  }

  get lockSettings(): boolean {
    return !!this._settings && this._settings.settingsAllowed === false;
  }

  get lockEdition(): boolean {
    return !!this._settings && this._settings.editionAllowed === false;
  }

  get showFilters(): boolean {
    return !!this._settings && this._settings.showFilters !== false;
  }

  /** l'activation des scenarii passe d'abord par la vision */
  get visionScenActive():boolean {
    if(this._settings?.widget?.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { // pas de scenar pour le globalFilter
      return false;
    }
    return !!this.model.currentVision?.useScenario;
  }

  /** ... puis par l'activation au niveau widget */
  get scenActive():boolean {
    if(this._settings?.widget?.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { // pas de scenar pour le globalFilter
      return false;
    }
    return !!this._settings.useScenarii;
  }

  /** ... et pas de label ni choix en mode solo */
  get scenActiveSolo():boolean {
    if(this._settings?.widget?.widgetKind === WIDGET_KIND.GLOBAL_FILTER) { // pas  pour le globalFilter
      return false;
    }
    return !!this._settings.useScenarii && !this._settings?.multipleScenarii;
  }


  get useScenarii(): boolean {
    return !!this._settings && this._settings.useScenarii !== false;
  }

  get excelUpdate(): boolean {
    return !!this._settings && this._settings.excelUpdate !== false;
  }

  get excelCreate(): boolean {
    return !!this._settings && this._settings.excelCreate !== false;
  }

  get excelNonUniqueShortCode(): boolean {
    return !!this._settings && this._settings.excelNonUniqueShortCode !== false;
  }

  get exportXLS(): boolean {
    return !!this._settings && this._settings.expXLS !== false;
  }

  get exportPDF(): boolean {
    return !!this._settings && this._settings.expPDF !== false;
  }

  // TODO TODO conditionner affichage selon globalFilter

  public setLockSettings(e: WizEvent): void {
    this._settings.settingsAllowed = !e.value;
  }

  public setLockEdition(e: WizEvent): void {
    this._settings.editionAllowed = !e.value;
  }

  public setShowFilters(e: WizEvent): void {
    this._settings.showFilters = e.value;
  }

  public setUseScenarii(e: WizEvent): void {
    this._settings.useScenarii = e.value;
  }

  public setExcelUpdate(e: WizEvent): void {
    this._settings.excelUpdate = e.value;
  }

  public setExcelCreate(e: WizEvent): void {
    this._settings.excelCreate = e.value;
  }

  public setExcelNonUniqueShortCode(e: WizEvent): void {
    this._settings.excelNonUniqueShortCode = e.value;
  }

  get useMultipleScenarii(): boolean {
    return !!this._settings && this._settings?.multipleScenarii !== false;
  }
  public setMultipleScenarii(e: WizEvent): void {
    this._settings.multipleScenarii = e.value;
  }

  get useUserShowScenarii(): boolean {
    return !!this._settings && this._settings?.userShowScenarii !== false;
  }
  public setUserShowScenarii(e: WizEvent): void {
    this._settings.userShowScenarii = e.value;
  }

  get useShowScenLabel(): boolean {
    return !!this._settings && this._settings?.showScenLabel !== false;
  }
  public setShowScenLabel(e: WizEvent): void {
    this._settings.showScenLabel = e.value;
  }

  public setExportXLS(e: WizEvent): void {
    this._settings.expXLS = e.value;
  }

  public setExportPDF(e: WizEvent): void {
    this._settings.expPDF = e.value;
  }


  get isMassDeleteVisible(): boolean {
    
    if(!this._settings.editionAllowed) {
      return false;
    }
    
    return !!this._settings && this._settings.widget.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA
  }

  get massDelete(): boolean {
    return !!this._settings && this._settings?.massDel !== false;
  }
  public setMassDelete(e: WizEvent): void {
    this._settings.massDel = e.value;
  }


  /**
   * activer l'édition en masse depuis les headers
   * seulement si le widget est éditable
   */
  get isMassEditVisible(): boolean {
    
    if(!this._settings.editionAllowed) {
      return false;
    }
    
    return !!this._settings && this._settings.widget.widgetGlobalKind === WIDGET_GLOBAL_KIND.BASELINE
  }

  get headerMassEdit(): boolean {
    return !!this._settings && this._settings.headerMassEdit !== false;
  }

  public setHeaderMassEdit(e: WizEvent): void {
    this._settings.headerMassEdit = e.value;
  }

  get noUserFilter(): boolean {
    return !!this._settings && this._settings.noUserFilter !== false;
  }

  public setNoUserFilter(e: WizEvent): void {
    this._settings.noUserFilter = e.value;
  }

  get showList(): boolean {
    return !!this._settings && this._settings.showList !== false;
  }

  public setShowList(e: WizEvent): void {
    this._settings.showList = e.value;
  }

  get showAdd(): boolean {
    return !!this._settings && this._settings.showAdd !== false;
  }

  public setShowAdd(e: WizEvent): void {
    this._settings.showAdd = e.value;
  }

  get showDelete(): boolean {
    return !!this._settings && this._settings.showDelete !== false;
  }

  public setShowDelete(e: WizEvent): void {
    this._settings.showDelete = e.value;
  }


  /**
   * activer l'édition en masse depuis les headers
   * seulement si le widget est éditable
   */
  get isGatlingEnable(): boolean {
    
    return !!this._settings && this._settings.widget.widgetKind === WIDGET_KIND.FD_FORM;
  }

  get isGatlingMode(): boolean {
    return !!this._settings && this._settings.gatlingMode !== false;
  }

  public setGatlingMode(e: WizEvent): void {
    this._settings.gatlingMode = e.value;
  }

  get isHidden(): boolean {
    return !!this._settings && this._settings.hidden !== false;
  }

  public setHidden(e: WizEvent): void {
    this._settings.hidden = e.value;
  }

  public get targetPlace(): string {
    return this._settings?.targetPlace || null
  }


  /** sur save, alimentation de l'objet de configuration */
  public onSave(): void {
    this._settings.excelSheet = this.tiExcelSheet.wizValue;
    this._settings.targetPlace = this.tiTargetPlace.wizValue;
    this.callback(this._settings);
    this.hide();
  }
}
